import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Dropdown } from 'semantic-ui-react'
import { DownloadService } from '~/api/services/downloadFiles'
import Layout from '~/components/Layout'
import TableComponent from '~/components/TableComponent'
import { formatMonthLabel } from '~/pages/Farmer/Dashboard/Tabs/MoohDashboard/MilkMoneyContext'
import { DashboardQuery } from '~/QueryHooks/DashboardQuery'
import { ManagerQuery } from '~/QueryHooks/ManagerQuery'
import { handleErrors } from '~/utils/helpers'

const ManagerMilkPayments: React.FC = () => {
  const { t } = useTranslation()
  const tableColumnHeaders = [
    {
      name: t('Billing month'),
      key: 'billingMonth',
      type: 'date',
      format: 'MMM YYYY'
    },
    {
      name: t('Total amount (in kg)'),
      key: 'amount',
      type: 'no-unit-number',
      decimalScale: 0
    },
    {
      name: t('Fat (in %)'),
      key: 'fat',
      type: 'no-unit-number'
    },
    {
      name: t('Protein (in %)'),
      key: 'protein',
      type: 'no-unit-number'
    },
    {
      name: t('Auszahlungspreis (in Rp./kg)'),
      key: 'price',
      type: 'no-unit-number'
    },
    {
      name: t('Auszahlungsb. (in CHF)'),
      key: 'total',
      type: 'no-unit-number'
    },
    {
      name: t('Actions')
    }
  ]

  const actions = [
    {
      popupMessage: t('Download milk bill'),
      type: 'icon',
      name: 'cloud download',
      size: 'large',
      actionType: 'download'
    }
  ]

  const [loadingAction, setLoadingAction] = useState({
    type: '',
    id: null,
    value: false,
    idKey: ''
  })

  const [dataFiltered, setDataFiltered] = useState({
    rows: 20,
    page: 0,
    month: ''
  })

  const {
    data: milkMoneyOptions,
    isLoading: isLoadingOptions
  } = DashboardQuery.getDashboardMilkMoneyOptions()

  const {
    data: invoices,
    isLoading: isLoading,
    isFetching: isFetchingData,
    refetch: fetchData
  } = ManagerQuery.getMilkInvoicesIndex({
    ...dataFiltered,
    page: dataFiltered.page === 0 ? dataFiltered.page : dataFiltered.page - 1
  })

  useEffect(() => {
    if(milkMoneyOptions?.farmers?.length > 0) {
      setDataFiltered({
        ...dataFiltered,
        month: milkMoneyOptions?.months[0]?.key.toString()
      })
    }
  }, [milkMoneyOptions])

  const monthOptions = (milkMoneyOptions?.months ?? []).map((m) => ({
    key: String(m.key),
    value: String(m.key),
    text: formatMonthLabel(m.key)
  }))

  useEffect(() => {
    fetchData()
  }, [dataFiltered])

  async function handleDownloadPdf(row, type) {
    setLoadingAction({
      type: type,
      id: row.id,
      value: true,
      idKey: 'id'
    })
    try {
      await DownloadService.downloadFileWithName(`milk-money/temp/milk-invoices/${row.id}/preview/pdf`)
    } catch (error) {
      handleErrors(error)
    }
    setLoadingAction({
      type: '',
      id: null,
      value: false,
      idKey: ''
    })
  }

  function handleActionClick(row, type) {
    switch (type) {
      case 'download':
        return handleDownloadPdf(row, type)
      default:
        return
    }
  }

  function renderTable() {
    return <TableComponent
      rows={rows}
      total={filtered}
      actions={actions}
      loadingAction={loadingAction}
      tableColumnHeaders={tableColumnHeaders}
      handleClickAction={(row, type) => handleActionClick(row, type)}
      isFetchingData={isFetchingData}
      dataFiltered={dataFiltered}
      handleChangeDataFiltered={(data) => setDataFiltered(data)}
      animated
    />
  }

  function renderFilters() {
    return <div style={{ display: 'flex', gap: 20 }}>
      <Dropdown
        selection
        options={monthOptions}
        value={dataFiltered.month}
        noResultsMessage={t('No results found....')}
        onChange={(_, { value }) => {
          setDataFiltered({
            ...dataFiltered,
            month: value as string
          })
        }}
        placeholder={t('Select month')}
          />
    </div>
  }

  const { filtered, rows } = invoices ? invoices : { filtered: 0, rows: [] }

  return <Layout
    title={t('Milk payments')}
    filters={renderFilters()}
    segmented
    isBusy={isLoading || isLoadingOptions}
  >
    {renderTable()}
  </Layout>
}

export default ManagerMilkPayments
